// ============================================================
// BackButton.tsx — the standard top-bar "← Back" (AppHeader leftSlot).
//
// Back = the place you were actually on (goBack → history.back; App.tsx's
// popstate handler flushes + restores it). Hidden when there's no in-app
// entry behind this one (currentSeq 0 — e.g. straight after login or a
// fresh deep link): there's nowhere to go back to, and the sidebar covers
// "go somewhere else".
//
// onClick overrides the default for screens whose Back sometimes means
// something local (Tasks: close the open task form); pair it with
// `forceShow` while that local step exists.
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { goBack } from '../../navigation/navigate'
import type { Place } from '../../navigation/placeUrl'

interface BackButtonProps {
  // goBack's fallback (only used if history disappears between render and click).
  fallback?: Place
  onClick?: () => void
  forceShow?: boolean
  className?: string
  children?: React.ReactNode
}

const BackButton: React.FC<BackButtonProps> = ({ fallback = { view: 'dashboard' }, onClick, forceShow = false, className, children }) => {
  const hasHistory = useCasevoStore((s) => s.currentSeq > 0)
  if (!hasHistory && !forceShow) return null
  return (
    <button
      type="button"
      onClick={onClick ?? (() => goBack(fallback))}
      className={className ?? 'text-casevo-light hover:text-white text-sm mr-2 transition-colors'}
    >
      {children ?? '← Back'}
    </button>
  )
}

export default BackButton
